import React, { useState, useEffect } from 'react';
import { User, Key, Save, Eye, EyeOff, Building2, MapPin, Map } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useAuth } from '../../contexts/AuthContext';
import { useAlert } from '../../components/AlertProvider';
import { PhoneInput } from '@/components/ui/PhoneInput';

type TabType = 'entreprise' | 'contact' | 'securite';

const BuyerSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('entreprise');
  const [showMapPreview, setShowMapPreview] = useState(false);
  const { user, setUser } = useAuthStore();
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useAlert();

  const buildAddressComplete = (address: string, postalCode: string, city: string) => {
    return [address, postalCode, city]
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean)
      .join(' ');
  };

  const buildMapsLink = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed ? `https://www.google.com/maps/search/${encodeURIComponent(trimmed)}` : '';
  };

  const buildMapsEmbedSrc = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed
      ? `https://www.google.com/maps?q=${encodeURIComponent(trimmed)}&output=embed`
      : '';
  };

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    maps_link: '',
    siret: '',
    payment_mode: 'Virement',
    payment_terms: '30 jours FDM',
    invoice_email: ''
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const [awaitingPasswordOtp, setAwaitingPasswordOtp] = useState(false);
  const [passwordOtpCode, setPasswordOtpCode] = useState('');
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) return;
    const id = window.setInterval(() => {
      setResendCooldownSeconds((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldownSeconds]);

  const fetchProfile = async () => {
    if (!token) {
      showError('Non authentifié');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/v1/clients/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setProfileForm({
        company_name: data.company_name || '',
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        city: data.city || '',
        postal_code: data.postal_code || '',
        country: data.country || '',
        maps_link: data.maps_link || '',
        siret: data.siret || '',
        payment_mode: data.payment_mode || 'Virement',
        payment_terms: data.payment_terms || '30 jours FDM',
        invoice_email: data.invoice_email || ''
      });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setSaving(true);
    try {
      const response = await fetch('/api/v1/clients/me', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileForm),
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      await response.json();

      // Update local auth state to reflect changes in Header
      if (user) {
        setUser({
          ...user,
          first_name: profileForm.first_name,
          last_name: profileForm.last_name || user.last_name,
          email: profileForm.email
        });
      }

      // Show success toast
      showSuccess('Vos informations ont été mises à jour avec succès');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour du profil');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      showError('Les nouveaux mots de passe ne correspondent pas');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      showError('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setChangingPassword(true);
    try {
      const response = await fetch('/api/v1/clients/me/password', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password,
          otp_code: awaitingPasswordOtp ? passwordOtpCode : undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Erreur ${response.status}`);
      }

      const data = await response.json();

      if (data?.requires_otp) {
        setAwaitingPasswordOtp(true);
        setOtpModalOpen(true);
        setResendCooldownSeconds(30);
        showInfo('Un code de vérification a été envoyé par email');
        return;
      }

      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setAwaitingPasswordOtp(false);
      setPasswordOtpCode('');
      setOtpModalOpen(false);
      setResendCooldownSeconds(0);

      showSuccess('Mot de passe mis à jour. Vous allez être déconnecté...');

      setTimeout(() => {
        logout();
        navigate('/client/login');
      }, 1500);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour du mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleResendPasswordOtp = async () => {
    if (!token) return;
    if (!passwordForm.current_password || !passwordForm.new_password) {
      showError('Veuillez remplir le mot de passe actuel et le nouveau mot de passe');
      return;
    }

    setChangingPassword(true);
    try {
      const response = await fetch('/api/v1/clients/me/password', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Erreur ${response.status}`);
      }

      const data = await response.json();
      if (data?.requires_otp) {
        setAwaitingPasswordOtp(true);
        setOtpModalOpen(true);
        setResendCooldownSeconds(30);
        showInfo('Un nouveau code de vérification a été envoyé par email');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : "Erreur lors de l'envoi du code");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleConfirmOtp = async () => {
    if (passwordOtpCode.length !== 6) {
      showError('Veuillez saisir un code à 6 chiffres');
      return;
    }
    await handlePasswordSubmit({ preventDefault: () => { } } as any);
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-green-50/20 to-slate-50 dark:from-slate-950 dark:via-green-950/10 dark:to-slate-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Chargement des paramètres...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-green-50/20 to-slate-50 dark:from-slate-950 dark:via-green-950/10 dark:to-slate-950">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-3 sm:px-6 sm:py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 via-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
            <User className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Paramètres</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Gérez votre profil et vos préférences
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="max-w-5xl mx-auto w-full px-3 py-4 sm:px-6 sm:py-6 flex flex-col flex-1 overflow-hidden">

          {/* Tabs */}
          <div className="flex gap-1 mb-4 sm:mb-6 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl flex-shrink-0">
            {[
              { id: 'entreprise' as TabType, label: 'Entreprise', icon: Building2 },
              { id: 'contact' as TabType, label: 'Contact & Adresse', icon: MapPin },
              { id: 'securite' as TabType, label: 'Sécurité', icon: Key },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${activeTab === tab.id
                  ? 'bg-white dark:bg-slate-900 text-green-600 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                  }`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-xl border border-slate-200/50 dark:border-slate-700/50 shadow-lg p-3 sm:p-6 flex-1 overflow-auto">
            {/* Entreprise Tab */}
            {activeTab === 'entreprise' && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-green-600" />
                  Informations Entreprise
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Nom de l'entreprise *
                    </label>
                    <input
                      type="text"
                      autoComplete="off"
                      value={profileForm.company_name}
                      onChange={(e) => setProfileForm({ ...profileForm, company_name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Prénom
                    </label>
                    <input
                      type="text"
                      autoComplete="off"
                      value={profileForm.first_name}
                      onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Nom
                    </label>
                    <input
                      type="text"
                      autoComplete="off"
                      value={profileForm.last_name}
                      onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      SIRET
                    </label>
                    <input
                      type="text"
                      autoComplete="off"
                      value={profileForm.siret}
                      onChange={(e) => setProfileForm({ ...profileForm, siret: e.target.value })}
                      placeholder="Ex: 123 456 789 00012"
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex justify-end pt-4">
                  <button
                    onClick={handleProfileSubmit}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-colors duration-200"
                  >
                    <Save className="h-4 w-4" />
                    {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                  </button>
                </div>
              </div>
            )}

            {/* Contact & Adresse Tab */}
            {activeTab === 'contact' && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <User className="h-5 w-5 text-green-600" />
                  Contact
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      autoComplete="off"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Téléphone
                    </label>
                    <PhoneInput
                      value={profileForm.phone}
                      onChange={(value) => setProfileForm({ ...profileForm, phone: value })}
                    />
                  </div>
                </div>

                <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                  <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2 mb-4">
                    <MapPin className="h-5 w-5 text-green-600" />
                    Adresse
                  </h2>
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Localisation</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label htmlFor="buyer_settings_address" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Rue</label>
                        <input
                          id="buyer_settings_address"
                          autoComplete="off"
                          className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                          value={profileForm.address}
                          onChange={(e) =>
                            setProfileForm((prev) => {
                              const next = { ...prev, address: e.target.value };
                              const addressComplete = buildAddressComplete(next.address, next.postal_code, next.city);
                              return { ...next, maps_link: buildMapsLink(addressComplete) };
                            })
                          }
                          placeholder="Ex: 44 Avenue Gambetta"
                        />
                      </div>

                      <div>
                        <label htmlFor="buyer_settings_postal_code" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Code postal</label>
                        <input
                          id="buyer_settings_postal_code"
                          autoComplete="off"
                          className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                          value={profileForm.postal_code}
                          onChange={(e) =>
                            setProfileForm((prev) => {
                              const next = { ...prev, postal_code: e.target.value };
                              const addressComplete = buildAddressComplete(next.address, next.postal_code, next.city);
                              return { ...next, maps_link: buildMapsLink(addressComplete) };
                            })
                          }
                          placeholder="Ex: 17100"
                        />
                      </div>

                      <div>
                        <label htmlFor="buyer_settings_city" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Ville</label>
                        <input
                          id="buyer_settings_city"
                          autoComplete="off"
                          className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                          value={profileForm.city}
                          onChange={(e) =>
                            setProfileForm((prev) => {
                              const next = { ...prev, city: e.target.value };
                              const addressComplete = buildAddressComplete(next.address, next.postal_code, next.city);
                              return { ...next, maps_link: buildMapsLink(addressComplete) };
                            })
                          }
                          placeholder="Ex: Saintes"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label htmlFor="buyer_settings_address_complete" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Adresse complète</label>
                        <input
                          id="buyer_settings_address_complete"
                          autoComplete="off"
                          className="w-full px-3 py-2 border rounded-lg bg-slate-50 dark:bg-slate-800/60 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
                          value={buildAddressComplete(profileForm.address, profileForm.postal_code, profileForm.city)}
                          readOnly
                        />

                        {buildAddressComplete(profileForm.address, profileForm.postal_code, profileForm.city) && (
                          <div className="mt-2 flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={() => setShowMapPreview((v) => !v)}
                              className={
                                "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                                (showMapPreview
                                  ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-900/40 dark:bg-green-950/40 dark:text-green-300'
                                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                              }
                            >
                              <Map className="h-3.5 w-3.5" />
                              Carte
                            </button>
                            {profileForm.maps_link && (
                              <a
                                href={profileForm.maps_link}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                Ouvrir dans Google Maps
                              </a>
                            )}
                          </div>
                        )}

                        {showMapPreview && (
                          <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                            <iframe
                              title="Google Maps"
                              src={buildMapsEmbedSrc(buildAddressComplete(profileForm.address, profileForm.postal_code, profileForm.city))}
                              className="w-full h-64"
                              referrerPolicy="no-referrer-when-downgrade"
                              allowFullScreen
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end pt-4">
                  <button
                    onClick={handleProfileSubmit}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-colors duration-200"
                  >
                    <Save className="h-4 w-4" />
                    {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                  </button>
                </div>
              </div>
            )}

            {/* Sécurité Tab */}
            {activeTab === 'securite' && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Key className="h-5 w-5 text-green-600" />
                  Changer le mot de passe
                </h2>
                <form onSubmit={handlePasswordSubmit} className="space-y-6 max-w-xl">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Mot de passe actuel *
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.current ? "text" : "password"}
                        autoComplete="current-password"
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                        className="w-full px-4 py-2 pr-12 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Nouveau mot de passe *
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.new ? "text" : "password"}
                          autoComplete="new-password"
                          value={passwordForm.new_password}
                          onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                          className="w-full px-4 py-2 pr-12 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          required
                          minLength={6}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Confirmer le nouveau mot de passe *
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.confirm ? "text" : "password"}
                          autoComplete="new-password"
                          value={passwordForm.confirm_password}
                          onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                          className="w-full px-4 py-2 pr-12 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          required
                          minLength={6}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={changingPassword}
                      className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-colors duration-200"
                    >
                      <Key className="h-4 w-4" />
                      {changingPassword ? 'Mise à jour...' : 'Changer le mot de passe'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>

      {
        otpModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-900 shadow-lg p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Vérification</h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                Un code a été envoyé par email. Entrez le code pour confirmer le changement de mot de passe.
              </p>

              <div className="mt-4">
                <label htmlFor="client_password_otp" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Code (6 chiffres)
                </label>
                <input
                  id="client_password_otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={passwordOtpCode}
                  onChange={(e) => setPasswordOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div className="mt-4 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setOtpModalOpen(false)}
                  className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  Annuler
                </button>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleResendPasswordOtp}
                    disabled={changingPassword || resendCooldownSeconds > 0}
                    className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50"
                  >
                    {resendCooldownSeconds > 0 ? `Renvoyer (${resendCooldownSeconds}s)` : 'Renvoyer'}
                  </button>

                  <button
                    type="button"
                    onClick={handleConfirmOtp}
                    disabled={changingPassword || passwordOtpCode.length !== 6}
                    className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    Confirmer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default BuyerSettings;
